// src/components/controller/ControllerModals.js
import { useStyletron } from 'baseui';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { Checkbox } from 'baseui/checkbox';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton } from 'baseui/modal';

import {
  ModalBodyContainer,
  SettingsIndentedContainer,
  ModalSectionHeader,
  EditorRowContainer,
  DeleteButtonOverrides,
} from '../../styles/controllerPageStyles';
import {
  CustomTextareaOverrides,
  createControllerModalOverrides,
  SettingsInputOverrides
} from '../../styles/controllerModalStyles';
import ScheduleTimerModal from './ScheduleTimerModal';

const ControllerModals = ({
  modals,
  setModals,

  tournamentDetails,
  editingInfoText,
  eventEditor,
  timerState,

  setEditingInfoText,
  setEventEditor,

  t,
  rows,
  tournamentActions,
  structureActions,
  handleScheduleTimer,
  controllerSettings,
  updateControllerSetting,
  updateInfoText,
}) => {
  const [css, theme] = useStyletron();

  const handleSaveEvents = () => {
    // Transform messages from { id: ..., text: ... } to { rowIndex, message: string }
    const transformedMessages = eventEditor.messages.map(msg => ({
      rowIndex: eventEditor.rowIndex,
      message: msg.text,
    }));
    structureActions.updateEventsForRow(eventEditor.rowIndex, transformedMessages);
    setModals(m => ({ ...m, event: false }));
  };

  const isPlayerSettingsDisabled = !tournamentDetails.showPlayerStats;

  return (
    <>
      <ScheduleTimerModal
        isOpen={modals.schedule}
        onClose={() => setModals(m => ({ ...m, schedule: false }))}
        onSchedule={handleScheduleTimer}
      />

      <Modal onClose={() => setModals(m => ({ ...m, settings: false }))} isOpen={modals.settings} overrides={createControllerModalOverrides('auto')}>
        <ModalHeader>{t('common.settings')}</ModalHeader>
        <ModalBody>
          <ModalBodyContainer>
            <ModalSectionHeader>{t('controller.viewSettingsTitle')}</ModalSectionHeader>
            <Checkbox
              checked={controllerSettings.isIntegratedViewEnabled}
              onChange={e => updateControllerSetting('isIntegratedViewEnabled', e.target.checked)}
            >
              {t('controller.enableIntegratedViewMode')}
            </Checkbox>

            <ModalSectionHeader>{t('controller.playerInfoSettingsTitle')}</ModalSectionHeader>
            <Checkbox checked={tournamentDetails.showPlayerStats} onChange={e => tournamentActions.updateTournamentDetails({ showPlayerStats: e.target.checked })}>
              {t('controller.enablePlayerStackSettings')}
            </Checkbox>
            <SettingsIndentedContainer>
              <Input type="number" value={tournamentDetails.initialStack} onChange={e => tournamentActions.updateTournamentDetails({ initialStack: parseInt(e.target.value, 10) || 0 })} placeholder={t('controller.enterInitialStack')} startEnhancer={t('controller.initialStackLabel')} overrides={SettingsInputOverrides} disabled={isPlayerSettingsDisabled} />
              <Input
                type="number"
                value={tournamentDetails.rebuyStack}
                onChange={e => {
                  const newStack = parseInt(e.target.value, 10) || 0;
                  const updates = { rebuyStack: newStack };
                  if (newStack === 0) {
                    updates.totalRebuys = 0;
                  }
                  tournamentActions.updateTournamentDetails(updates);
                }}
                placeholder={t('controller.enterRebuyStack')}
                startEnhancer={t('controller.rebuyStackLabel')}
                overrides={SettingsInputOverrides}
                disabled={isPlayerSettingsDisabled}
              />
              <Input
                type="number"
                value={tournamentDetails.addonStack}
                onChange={e => {
                  const newStack = parseInt(e.target.value, 10) || 0;
                  const updates = { addonStack: newStack };
                  if (newStack === 0) {
                    updates.totalAddons = 0;
                  }
                  tournamentActions.updateTournamentDetails(updates);
                }}
                placeholder={t('controller.enterAddonStack')}
                startEnhancer={t('controller.addonStackLabel')}
                overrides={SettingsInputOverrides}
                disabled={isPlayerSettingsDisabled}
              />
            </SettingsIndentedContainer>

            <div style={{ marginTop: '32px', textAlign: 'center', opacity: 0.7, borderTop: '1px solid #333', paddingTop: '24px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>Blind Pace</h4>
              <p style={{ margin: 0, fontSize: '12px' }}>©2026 Necron</p>
            </div>
          </ModalBodyContainer>
        </ModalBody>
        <ModalFooter><ModalButton onClick={() => setModals(m => ({ ...m, settings: false }))}>{t('common.close')}</ModalButton></ModalFooter>
      </Modal>

      <Modal onClose={() => setModals(m => ({ ...m, info: false }))} isOpen={modals.info} overrides={createControllerModalOverrides('320px')}>
        <ModalHeader>{t('display.editInfoTitle')}</ModalHeader>
        <ModalBody>
          <textarea
            className={css(CustomTextareaOverrides.Input.style({ $theme: theme }))}
            value={editingInfoText}
            onChange={e => { setEditingInfoText(e.target.value); }}
            placeholder={t('display.enterCustomInfoTextPlaceholder')}
            rows={6}
          />
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="tertiary" onClick={() => setModals(m => ({ ...m, info: false }))}>{t('common.modalCancel')}</ModalButton>
          <ModalButton onClick={() => { updateInfoText(editingInfoText); setModals(m => ({ ...m, info: false })); }}>{t('common.modalSave')}</ModalButton>
        </ModalFooter>
      </Modal>
      <Modal onClose={() => setModals(m => ({ ...m, event: false }))} isOpen={modals.event} overrides={createControllerModalOverrides('auto')}>
        <ModalHeader>{t('controller.messageEditTitleRow', { row: eventEditor.rowIndex !== null ? rows[eventEditor.rowIndex]?.level || t('common.break') : '' })}</ModalHeader>
        <ModalBody>
          <ModalBodyContainer>
            {eventEditor.messages.map((msg, index) => (
              <EditorRowContainer key={msg.id}>
                <Input
                  value={msg.text}
                  onChange={e => {
                    const newMessages = [...eventEditor.messages];
                    newMessages[index].text = e.currentTarget.value;
                    setEventEditor(e => ({ ...e, messages: newMessages }));
                  }}
                  placeholder={t('controller.inputMessage')}
                />
                <Button
                  size="compact"
                  kind="minimal"
                  onClick={() => setEventEditor(e => ({ ...e, messages: e.messages.filter(m => m.id !== msg.id) }))}
                  overrides={DeleteButtonOverrides}
                >{t('controller.deleteMessage')}</Button>
              </EditorRowContainer>
            ))}
            <Button onClick={() => setEventEditor(e => ({ ...e, messages: [...e.messages, { id: Math.random(), text: '' }] }))}>{t('controller.addMessage')}</Button>
          </ModalBodyContainer>
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="tertiary" onClick={() => setModals(m => ({ ...m, event: false }))}>{t('common.modalCancel')}</ModalButton>
          <ModalButton onClick={handleSaveEvents}>{t('common.modalSave')}</ModalButton>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ControllerModals;
